/**
 * use-project-group-by.ts
 *
 * Manages the group-by selection for the Projects list view.
 * Supports multiple simultaneous group-by fields (ordered array).
 * State is persisted in sessionStorage so it survives navigation within
 * the same browser tab but resets on a fresh load.
 */

import { useState, useCallback } from 'react';
import type { GroupById } from '@/lib/project-group-by-utils';
import { MAX_GROUP_BY_LEVELS } from '@/lib/project-group-by-utils';

const STORAGE_KEY = 'projects-group-by-v2';

function readStored(): GroupById[] {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) return (parsed as GroupById[]).slice(0, MAX_GROUP_BY_LEVELS);
    }
  } catch {
    // ignore
  }
  return [];
}

export function useProjectGroupBy() {
  const [groupBy, setGroupByState] = useState<GroupById[]>(readStored);

  const setGroupBy = useCallback((value: GroupById[]) => {
    const capped = value.slice(0, MAX_GROUP_BY_LEVELS);
    setGroupByState(capped);
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(capped));
    } catch {
      // ignore storage errors
    }
  }, []);

  /** Toggle a single field in/out of the group-by array — respects MAX_GROUP_BY_LEVELS */
  const toggleGroupBy = useCallback((field: GroupById) => {
    setGroupByState((prev) => {
      if (!prev.includes(field) && prev.length >= MAX_GROUP_BY_LEVELS) return prev;
      const next = prev.includes(field)
        ? prev.filter((f) => f !== field)
        : [...prev, field];
      try {
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify(next));
      } catch {
        // ignore
      }
      return next;
    });
  }, []);

  return { groupBy, setGroupBy, toggleGroupBy };
}
